import { international } from '@formatters/dateize';
import { depatternize } from '@formatters/patternize';

import invalidCPF from '@validators/cpf';

import BANKS from '@js/constants/banks';

import filled from '@/components/Form/javascripts/filled';

const HONDA_INSTALLMENTS = 55;
export const DEFAULT_INSTALLMENTS = [12, 24, 36, 48, HONDA_INSTALLMENTS, 60];

const ALLOWED_UTMS = ['id', 'source', 'source_platform', 'medium', 'campaign', 'content', 'term'].map((i) => `utm_${i}`);

function parseCondition(json) {
  if (!filled(json.min_return)) {
    delete json.min_return;
  }

  if (!filled(json.max_return)) {
    delete json.max_return;
  } else if (json.max_return < json.min_return) {
    json.max_return = json.min_return;
  }

  if (json.installments === HONDA_INSTALLMENTS) {
    json.bank_febraban_code = BANKS.HONDA.code;
  }

  delete json._enable_custom_installments;
  delete json._enable_custom_settings;

  return json;
}

function parseConditions(json, condition, props) {
  let conditions = [];

  if (props.settings?.simulation?.conditions?.length) {
    conditions = props.settings?.simulation.conditions;
  } else if (condition?.installments) {
    conditions = String(condition.installments || '')
      .split(',')
      .filter((i) => i)
      .map((i) => ({ installments: i.trim() }));
  }

  conditions = conditions
    .filter(({ installments: x }) => x && x === depatternize(x) && x > 0 && x <= 120)
    .sort((a, b) => a - b)
    .slice(0, 12);

  if (!conditions?.length) {
    const honda = props.banks.includes(BANKS.HONDA.code);

    conditions = DEFAULT_INSTALLMENTS
      .filter((i) => (honda ? true : i !== HONDA_INSTALLMENTS))
      .map((i) => ({ installments: i }));
  }

  json.conditions = conditions.map((i) => parseCondition({
    ...condition,
    ...i,
    down_payment: condition?.down_payment || i?.down_payment || 0,
    products_options: {
      ...condition?.products_options,
      ...i?.products_options,
    },
  }));
}

function parseVehicle(json, store) {
  if (json.zero_km === true) {
    json.licensing_uf = store?.uf || json.licensing_uf;
    json.licensing_city = store?.city || json.licensing_city;
  }

  if (/-/.test(json.credere_vehicle_model_id)) {
    json.vehicle_molicar_code = json.credere_vehicle_model_id;

    delete json.credere_vehicle_model_id;
  }

  delete json.name;
}

function parseSeller(json) {
  if (filled(json.credere_seller_id) && !invalidCPF(json.credere_seller_id)) {
    json.seller_cpf = json.credere_seller_id;

    delete json.credere_seller_id;
  }
}

function parseLead(json) {
  json.birthdate = international(json.birthdate);
  json.phone_number = json.phone_number ? `+55${depatternize(json.phone_number)}` : undefined;

  if (json.retrieve_occupation) {
    json.retrieve_occupation = String(json.retrieve_occupation);
  }
}

function parsePayload(json) {
  json.simulation.payload = json.simulation.payload || {};

  const { referrer } = window.CREDERE._INTERNAL || {};

  if (!referrer) {
    delete json.simulation.payload.referrer;

    return;
  }

  const query = Object.fromEntries(new URL(referrer).searchParams);
  json.simulation.payload.referrer = referrer;

  Object
    .keys(query)
    .filter((i) => ALLOWED_UTMS.includes(i))
    .forEach((key) => {
      json.simulation.payload[key] = query[key];
    });
}

function parseTrackers(json) {
  if (!json.simulation.external_tracker_uuid) {
    delete json.simulation.external_tracker_uuid;
  }
}

export default function parser(json, props) {
  const clean = { ...json };

  const bankSgtd = props.settings?.simulation?.process_bank_suggested_conditions;
  clean.simulation.process_bank_suggested_conditions = filled(bankSgtd) ? bankSgtd : true;

  const credereSgtd = props.settings?.simulation?.process_credere_suggested_conditions;
  clean.simulation.process_credere_suggested_conditions = filled(credereSgtd) ? credereSgtd : false;

  clean.simulation.bank_febraban_codes = props.banks;

  parseLead(clean.lead);
  parseConditions(clean.simulation, clean.simulation._condition, props);
  parsePayload(clean);
  parseTrackers(clean);

  clean.simulation.retrieve_lead = { cpf_cnpj: clean.lead.cpf_cnpj };

  const storeID = parseInt(clean.simulation.store_id, 10);
  parseVehicle(clean.simulation.vehicle, props.domains?.stores?.find((i) => i.id === storeID));

  parseSeller(clean.simulation);

  delete clean.simulation._accept;
  delete clean.simulation._condition;

  return clean;
}

parser.config = function config(parsed) {
  return {
    headers: {
      'store-id': parsed.simulation.store_id,
    },
  };
};
